import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, Link as LinkIcon, Save, ArrowLeft, Loader2, Download, Wand2, Copy, GripVertical, ChevronRight, ExternalLink } from "lucide-react";
import Nav from "@/components/Nav";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DestinationAutocomplete from "@/components/DestinationAutocomplete";
import { BLOCK_TYPES, BLOCK_ICON, BLOCK_LABEL, newBlock, youtubeId } from "@/components/proposal/blocks";
import { PlacesAutocomplete } from "@/components/quote/PlacesAutocomplete";
import { AttachmentUploader } from "@/components/quote/AttachmentUploader";

const STATUS = [
  { value: "draft", label: "Brouillon" },
  { value: "sent", label: "Envoyé" },
  { value: "accepted", label: "Accepté" },
  { value: "expired", label: "Expiré" },
];

export default function AdminProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proposal, setProposal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const dirtyRef = useRef(false);
  // Always points at the freshest proposal so a debounced save never sends stale data.
  const proposalRef = useRef(null);
  const savingRef = useRef(false);

  useEffect(() => { proposalRef.current = proposal; }, [proposal]);

  useEffect(() => {
    api.get(`/admin/proposals/${id}`)
      .then((r) => setProposal(r.data))
      .catch(() => toast.error("Devis introuvable."))
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-save (debounced)
  useEffect(() => {
    if (!proposal || !dirtyRef.current) return;
    const t = setTimeout(() => save(false), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal]);

  const set = (patch) => {
    setProposal((p) => ({ ...p, ...patch }));
    dirtyRef.current = true;
  };

  const save = async (manual = true) => {
    const current = proposalRef.current;
    if (!current || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    // Clear dirty up-front: any edit made WHILE the request is in flight re-sets it,
    // which is our signal not to overwrite the fresher local state with the server echo.
    dirtyRef.current = false;
    try {
      const payload = { ...current };
      delete payload.id;
      delete payload.slug;
      delete payload.created_at;
      delete payload.updated_at;
      const { data } = await api.patch(`/admin/proposals/${id}`, payload);
      // Only apply the server response if nothing changed during the round-trip —
      // otherwise we'd wipe a just-typed video URL / just-added attachment.
      if (!dirtyRef.current) setProposal(data);
      if (manual) toast.success("Devis enregistré.");
    } catch (e) {
      dirtyRef.current = true; // keep dirty so the next autosave retries
      toast.error("Échec de l'enregistrement.");
    } finally {
      savingRef.current = false;
      setSaving(false);
      // If edits arrived during the round-trip (or a save was skipped while one was in flight),
      // flush them — the debounced effect only re-arms on a new proposal change.
      if (dirtyRef.current) setTimeout(() => save(false), 700);
    }
  };

  const toggleCarnetPublic = async (next) => {
    if (next && !(proposal.cover_image || "").trim()) {
      toast.error("Ajoutez une image de couverture avant de publier le carnet.");
      return;
    }
    try {
      if (next) await save(false); // persist latest edits before publishing
      const { data } = await api.post(`/admin/proposals/${id}/${next ? "publish-carnet" : "unpublish-carnet"}`);
      setProposal(data);
      toast.success(next ? "Carnet publié publiquement." : "Carnet retiré du public.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible sur le carnet.");
    }
  };


  const addBlock = (type) => {
    set({ blocks: [...(proposal.blocks || []), newBlock(type)] });
  };

  // Update a block by its stable id — safe even if the list is reordered/removed
  // while an upload is still in flight. Patch can be an object or fn(block)=>partial.
  const updateBlockById = (blockId, patch) => {
    setProposal((p) => ({
      ...p,
      blocks: p.blocks.map((b) => {
        if (b.id !== blockId) return b;
        const applied = typeof patch === "function" ? patch(b) : patch;
        return { ...b, ...applied };
      }),
    }));
    dirtyRef.current = true;
  };

  const removeBlock = (idx) => {
    setProposal((p) => ({ ...p, blocks: p.blocks.filter((_, i) => i !== idx) }));
    dirtyRef.current = true;
  };

  const moveBlock = (idx, dir) => {
    setProposal((p) => {
      const blocks = [...p.blocks];
      const j = idx + dir;
      if (j < 0 || j >= blocks.length) return p;
      [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
      return { ...p, blocks };
    });
    dirtyRef.current = true;
  };

  const duplicateBlock = (idx) => {
    setProposal((p) => {
      const blocks = [...p.blocks];
      const copy = { ...blocks[idx], id: Math.random().toString(36).slice(2, 10) };
      blocks.splice(idx + 1, 0, copy);
      return { ...p, blocks };
    });
    dirtyRef.current = true;
    toast.success("Bloc dupliqué.");
  };

  // Apply a proposal returned by the server (already persisted) WITHOUT marking dirty.
  // Used by atomic endpoints like /blocks/add-image so we don't trigger a heavy full-PATCH auto-save.
  const applyServerProposal = (data) => {
    setProposal(data);
    dirtyRef.current = false;
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    setProposal((p) => {
      const blocks = [...p.blocks];
      const [moved] = blocks.splice(result.source.index, 1);
      blocks.splice(result.destination.index, 0, moved);
      return { ...p, blocks };
    });
    dirtyRef.current = true;
  };

  const scrapeHotel = async () => {
    if (!scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const { data } = await api.post("/admin/proposals/tools/scrape-hotel", { url: scrapeUrl });
      const block = newBlock("hotel");
      block.title = data.name || "Hôtel";
      block.subtitle = data.address || null;
      block.description = data.description || null;
      block.location = data.address || null;
      block.address = data.address || null;
      block.map_query = data.address || data.name || null;
      block.images = data.images || [];
      block.rating = data.rating || null;
      block.amenities = data.amenities || [];
      set({ blocks: [...(proposal.blocks || []), block] });
      setScrapeUrl("");
      toast.success("Hôtel importé avec succès.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import impossible.";
      toast.error(typeof detail === "string" ? detail : "Import impossible.");
    } finally {
      setScraping(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/proposals/tools/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ cover_image: `/api/public/files/${data.storage_path}` });
      toast.success("Image de couverture importée.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Import impossible.";
      toast.error(typeof detail === "string" ? detail : "Import impossible.");
    } finally {
      setCoverUploading(false);
    }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim() || aiBusy) return;
    if (proposal.blocks?.length > 0 && !window.confirm("Cela remplacera le contenu actuel du devis. Continuer ?")) return;
    setAiBusy(true);
    try {
      const { data } = await api.post(`/admin/proposals/${id}/ai-generate`, { prompt: aiPrompt });
      setProposal(data);
      dirtyRef.current = false;
      toast.success("Devis généré par l'IA. Affinez à votre guise.");
      setAiPrompt("");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Génération IA indisponible.";
      toast.error(typeof detail === "string" ? detail : "Erreur.");
    } finally {
      setAiBusy(false);
    }
  };

  const totals = useMemo(() => {
    if (!proposal?.blocks) return { pub: 0, deal: 0 };
    return proposal.blocks.reduce(
      (acc, b) => ({
        pub: acc.pub + (Number(b.public_price) || 0),
        deal: acc.deal + (Number(b.deal_price) || 0),
      }),
      { pub: 0, deal: 0 }
    );
  }, [proposal]);

  const previewUrl = proposal ? `/devis/${proposal.slug}` : "#";
  const fullPreviewUrl = proposal ? `${window.location.origin}/devis/${proposal.slug}` : "";

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  if (!proposal) return null;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Nav />
      <main className="pt-28 pb-24 px-6 md:px-12 max-w-[1400px] mx-auto">
        {/* Toolbar */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/proposals")} className="p-2 hover:bg-white"><ArrowLeft className="h-4 w-4" /></button>
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#96836B]">Devis</p>
              <p className="font-serif-display text-2xl">{proposal.title || "Sans titre"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={proposal.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="rounded-none w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={() => { navigator.clipboard.writeText(fullPreviewUrl); toast.success("Lien copié."); }} className="px-3 py-2 border border-[#EAE8E3] hover:border-[#1A1B18] text-xs uppercase tracking-wider flex items-center gap-1.5">
              <LinkIcon className="h-3.5 w-3.5" /> Copier le lien
            </button>
            <Link to={previewUrl} target="_blank" className="px-3 py-2 border border-[#1A1B18] hover:bg-[#1A1B18] hover:text-white text-xs uppercase tracking-wider flex items-center gap-1.5" data-testid="btn-preview">
              <Eye className="h-3.5 w-3.5" /> Prévisualiser
            </Link>
            <Button onClick={() => save(true)} disabled={saving} data-testid="btn-save" className="rounded-none bg-[#1A1B18] hover:bg-[#96836B] text-white h-9 text-[10px] uppercase tracking-[0.22em]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-3.5 w-3.5 mr-2" /> Enregistrer</>}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Settings */}
          <aside className="lg:col-span-1 space-y-5">

            {/* AI Assistant */}
            <section className="bg-[#1A1B18] text-white p-6 space-y-4">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-[#E4DCCF]" strokeWidth={1.5} />
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#E4DCCF]">Assistant IA</p>
              </div>
              <p className="text-sm text-white/80 leading-relaxed">
                Décrivez le voyage en quelques phrases — Claude génère titre, intro, timeline jour par jour, vols, transferts, hôtels, activités et conditions.
              </p>
              <Textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={5}
                placeholder="Ex. Lune de miel 10 jours en Polynésie, 3 nuits Tahiti, 4 nuits Moorea, 3 nuits Bora Bora, départ Lyon, luxe, transferts privés + snorkeling"
                className="bg-white/10 border border-white/20 text-white placeholder:text-white/40 rounded-none resize-none focus:border-[#E4DCCF] focus-visible:ring-0"
                data-testid="ai-prompt"
              />
              <Button
                onClick={aiGenerate}
                disabled={aiBusy || !aiPrompt.trim()}
                data-testid="btn-ai-generate"
                className="w-full rounded-none bg-[#E4DCCF] hover:bg-white text-[#1A1B18] h-10 text-[10px] uppercase tracking-[0.22em]"
              >
                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Wand2 className="h-3.5 w-3.5 mr-2" /> Générer le devis</>}
              </Button>
              {(proposal.blocks || []).length > 0 && (
                <p className="text-[10px] text-white/50">La génération remplace le contenu actuel. Confirmation demandée.</p>
              )}
            </section>

            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4">
              <p className="label-eyebrow">Informations</p>
              <Field label="Titre"><Input value={proposal.title || ""} onChange={(e) => set({ title: e.target.value })} className="luxury-input" /></Field>
              <Field label="Sous-titre / accroche"><Input value={proposal.subtitle || ""} onChange={(e) => set({ subtitle: e.target.value })} className="luxury-input" placeholder="Ex. 11 jours d'évasion entre lagons et motus" data-testid="proposal-subtitle" /></Field>
              <Field label="Destination">
                <DestinationAutocomplete value={proposal.destination || ""} onChange={(v) => set({ destination: v })} placeholder="Ex. Polynésie française, Tahiti…" />
              </Field>
              <Field label="Ville de départ">
                <DestinationAutocomplete value={proposal.departure_city || ""} onChange={(v) => set({ departure_city: v })} placeholder="Ex. Lyon, Paris…" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date départ"><Input type="date" value={proposal.departure_date || ""} onChange={(e) => set({ departure_date: e.target.value })} className="luxury-input" /></Field>
                <Field label="Date retour"><Input type="date" value={proposal.return_date || ""} onChange={(e) => set({ return_date: e.target.value })} className="luxury-input" /></Field>
              </div>
              <Field label="Voyageurs"><Input value={proposal.travelers || ""} onChange={(e) => set({ travelers: e.target.value })} className="luxury-input" placeholder="Ex. 2 adultes, 1 enfant" /></Field>
              <Field label="Client (nom)"><Input value={proposal.client_name || ""} onChange={(e) => set({ client_name: e.target.value })} className="luxury-input" /></Field>
              <Field label="Email client"><Input type="email" value={proposal.client_email || ""} onChange={(e) => set({ client_email: e.target.value })} className="luxury-input" /></Field>
              <div className="space-y-1.5">
                <Label className="label-eyebrow">Image de couverture · bannière</Label>
                {/* Aperçu type bannière (Hero) — reflète l'affichage client */}
                {proposal.cover_image ? (
                  <div className="relative w-full aspect-[16/9] border border-[#EAE8E3] overflow-hidden bg-[#1A1B18]">
                    <img src={proposal.cover_image} alt="Couverture" className="w-full h-full object-cover" data-testid="cover-preview" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-[8px] uppercase tracking-[0.22em] text-[#E4DCCF]">Présentation du voyage</p>
                      <p className="font-serif-display text-white text-lg leading-tight line-clamp-2 mt-0.5">{proposal.title || "Titre du devis"}</p>
                      {proposal.subtitle && <p className="text-white/85 text-xs mt-0.5 line-clamp-1">{proposal.subtitle}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => set({ cover_image: null })}
                      title="Retirer"
                      data-testid="cover-remove"
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-[#1A1B18]/85 text-white text-sm flex items-center justify-center hover:bg-[#1A1B18]"
                    >×</button>
                  </div>
                ) : (
                  <div className="relative w-full aspect-[16/9] border border-[#EAE8E3] overflow-hidden bg-[#1A1B18] flex items-end" data-testid="cover-placeholder">
                    <div className="p-3">
                      <p className="text-[8px] uppercase tracking-[0.22em] text-[#E4DCCF]">Présentation du voyage</p>
                      <p className="font-serif-display text-white text-lg leading-tight line-clamp-2 mt-0.5">{proposal.title || "Titre du devis"}</p>
                      {proposal.subtitle && <p className="text-white/70 text-xs mt-0.5 line-clamp-1">{proposal.subtitle}</p>}
                      <p className="text-white/40 text-[9px] uppercase tracking-[0.18em] mt-2">Sans image · bannière sombre par défaut</p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                    disabled={coverUploading}
                    onChange={(e) => { const f = e.target.files?.[0]; uploadCover(f); e.target.value = ""; }}
                    className="text-xs file:mr-2 file:px-3 file:py-1.5 file:border file:border-[#1A1B18] file:bg-white hover:file:bg-[#1A1B18] hover:file:text-white file:cursor-pointer file:text-[10px] file:uppercase file:tracking-wider file:rounded-none"
                    data-testid="cover-upload"
                  />
                  {coverUploading && <Loader2 className="h-4 w-4 animate-spin text-[#96836B]" />}
                </div>
                <p className="text-[10px] text-[#737373]">JPG · PNG · WEBP · PDF (1ʳᵉ page) · 5 Mo max — affichée en bannière chez le client (ordinateur & mobile)</p>
                <Input value={proposal.cover_image && (proposal.cover_image.startsWith("data:") || proposal.cover_image.includes("/public/files/")) ? "" : (proposal.cover_image || "")} onChange={(e) => set({ cover_image: e.target.value })} className="luxury-input" placeholder="…ou collez une URL https://" data-testid="cover-url" />
              </div>
              <Field label="Introduction éditoriale"><Textarea value={proposal.intro || ""} onChange={(e) => set({ intro: e.target.value })} className="luxury-input resize-none" rows={4} placeholder="Quelques lignes pour planter le décor…" /></Field>
            </section>

            {/* CARNET PUBLIC (SEO) */}
            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4" data-testid="carnet-public-section">
              <div className="flex items-center justify-between gap-3">
                <p className="label-eyebrow">Carnet de voyage public</p>
                <button
                  type="button"
                  data-testid="carnet-public-toggle"
                  onClick={() => toggleCarnetPublic(!proposal.is_public)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${proposal.is_public ? "bg-[#1A1B18]" : "bg-[#D9D5CC]"}`}
                  aria-pressed={!!proposal.is_public}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${proposal.is_public ? "translate-x-5" : ""}`} />
                </button>
              </div>
              <p className="text-[11px] text-[#737373] leading-relaxed">
                Publie une version <strong>anonymisée</strong> de ce carnet (sans nom ni email client, sans références de réservation ni pièces jointes) sur la page publique « Les Carnets de Voyage » — indexable SEO.
              </p>
              {!(proposal.cover_image || "").trim() && !proposal.is_public && (
                <p className="text-[11px] text-[#7A4B3B]" data-testid="carnet-cover-warning">⚠ Une image de couverture est requise avant de publier le carnet.</p>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Type de voyage"><Input value={proposal.trip_type || ""} onChange={(e) => set({ trip_type: e.target.value })} className="luxury-input" placeholder="Ex. Voyage de noces" data-testid="carnet-trip-type" /></Field>
                <Field label="Durée"><Input value={proposal.duration_label || ""} onChange={(e) => set({ duration_label: e.target.value })} className="luxury-input" placeholder="Ex. 10 jours" data-testid="carnet-duration" /></Field>
              </div>
              <Field label="Intro (carte bibliothèque)"><Textarea value={proposal.public_intro || ""} onChange={(e) => set({ public_intro: e.target.value })} className="luxury-input resize-none" rows={2} placeholder="Résumé court affiché sur la carte du carnet" data-testid="carnet-public-intro" /></Field>
              {proposal.is_public && proposal.public_slug && (
                <a href={`/carnets/${proposal.public_slug}`} target="_blank" rel="noopener noreferrer" data-testid="carnet-public-link"
                  className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#96836B] hover:text-[#1A1B18] transition-colors">
                  <ExternalLink className="h-3.5 w-3.5" /> Voir le carnet public
                </a>
              )}
            </section>

            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4">
              <p className="label-eyebrow">Tarification globale</p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Prix public"><Input type="number" value={proposal.total_public_price ?? ""} onChange={(e) => set({ total_public_price: e.target.value ? parseFloat(e.target.value) : null })} className="luxury-input" /></Field>
                <Field label="Prix Deal Travel"><Input type="number" value={proposal.total_deal_price ?? ""} onChange={(e) => set({ total_deal_price: e.target.value ? parseFloat(e.target.value) : null })} className="luxury-input" /></Field>
              </div>
              <Field label="Devise">
                <Select value={proposal.currency} onValueChange={(v) => set({ currency: v })}>
                  <SelectTrigger className="luxury-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{["EUR","USD","GBP","CHF","MAD","XOF"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <p className="text-xs text-[#737373]">
                Somme des blocs : public {totals.pub.toLocaleString("fr-FR")} · Deal {totals.deal.toLocaleString("fr-FR")} {proposal.currency}
              </p>
              <button
                onClick={() => set({ total_public_price: totals.pub || null, total_deal_price: totals.deal || null })}
                className="text-[10px] uppercase tracking-[0.22em] text-[#96836B] hover:text-[#1A1B18]"
              >
                Recalculer depuis les blocs
              </button>
            </section>

            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4">
              <p className="label-eyebrow">Conditions générales</p>
              <Textarea
                value={proposal.conditions || ""}
                onChange={(e) => set({ conditions: e.target.value })}
                rows={5}
                className="luxury-input resize-none"
                placeholder="Conditions générales, paiement, mentions légales…"
              />
            </section>

            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4">
              <p className="label-eyebrow">Politique d&apos;annulation</p>
              <Textarea
                value={proposal.cancellation_policy || ""}
                onChange={(e) => set({ cancellation_policy: e.target.value })}
                rows={5}
                className="luxury-input resize-none"
                placeholder="Conditions d'annulation, modification, remboursement…"
              />
            </section>

            <section className="bg-white border border-[#EAE8E3] p-6 space-y-4">
              <p className="label-eyebrow">Informations pratiques</p>
              <Textarea
                value={proposal.practical_info || ""}
                onChange={(e) => set({ practical_info: e.target.value })}
                rows={6}
                className="luxury-input resize-none font-mono text-xs"
                placeholder="Markdown : ## Formalités, ## Monnaie, ## Météo, ## Prises, ## Sécurité, ## Fuseau horaire, ## Numéros utiles"
              />
            </section>
          </aside>

          {/* Right: Blocks */}
          <div className="lg:col-span-2 space-y-5">
            <section className="bg-white border border-[#EAE8E3] p-6">
              <div className="flex items-center justify-between mb-4">
                <p className="label-eyebrow">Blocs du voyage</p>
                <span className="text-xs text-[#737373]">{(proposal.blocks || []).length} bloc(s)</span>
              </div>

              <div className="flex flex-wrap gap-2">
                {BLOCK_TYPES.map((b) => {
                  const Icon = b.icon;
                  return (
                    <button
                      key={b.type}
                      data-testid={`add-block-${b.type}`}
                      onClick={() => addBlock(b.type)}
                      className="text-xs px-3 py-2 border border-[#EAE8E3] hover:border-[#1A1B18] hover:bg-[#FAF9F6] flex items-center gap-1.5 transition-colors"
                    >
                      <Icon className="h-3.5 w-3.5 text-[#96836B]" />
                      <Plus className="h-3 w-3" />
                      {b.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 pt-4 border-t border-[#EAE8E3]">
                <p className="label-eyebrow">Import rapide hôtel par URL</p>
                <div className="mt-2 flex gap-2">
                  <Input
                    value={scrapeUrl}
                    onChange={(e) => setScrapeUrl(e.target.value)}
                    placeholder="https://www.exemple-hotel.com"
                    className="luxury-input flex-1"
                    data-testid="hotel-scrape-url"
                  />
                  <Button
                    onClick={scrapeHotel}
                    disabled={scraping || !scrapeUrl.trim()}
                    data-testid="btn-scrape-hotel"
                    className="rounded-none bg-[#96836B] hover:bg-[#1A1B18] text-white px-4 text-[10px] uppercase tracking-[0.22em]"
                  >
                    {scraping ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1.5" /> Importer</>}
                  </Button>
                </div>
              </div>
            </section>

            {(proposal.blocks || []).length === 0 ? (
              <div className="border border-dashed border-[#EAE8E3] p-12 text-center bg-white">
                <p className="text-sm text-[#737373]">Ajoutez votre premier bloc ci-dessus pour commencer à raconter le voyage.</p>
              </div>
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="blocks">
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-4">
                      {proposal.blocks.map((b, idx) => (
                        <Draggable key={b.id} draggableId={b.id} index={idx}>
                          {(prov, snap) => (
                            <div ref={prov.innerRef} {...prov.draggableProps} className={snap.isDragging ? "opacity-90 shadow-2xl" : ""}>
                              <BlockEditor
                                idx={idx}
                                block={b}
                                total={proposal.blocks.length}
                                onChange={(patch) => updateBlockById(b.id, patch)}
                                onRemove={() => removeBlock(idx)}
                                onMove={(dir) => moveBlock(idx, dir)}
                                onDuplicate={() => duplicateBlock(idx)}
                                onServerUpdate={applyServerProposal}
                                proposalId={id}
                                dragHandle={prov.dragHandleProps}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow">{label}</Label>
    {children}
  </div>
);

const BlockEditor = ({ idx, block, total, onChange, onRemove, onMove, onDuplicate, onServerUpdate, proposalId, dragHandle }) => {
  const Icon = BLOCK_ICON[block.type] || BLOCK_ICON.note;
  const label = BLOCK_LABEL[block.type] || "Bloc";
  const setImages = (text) => onChange({ images: text.split("\n").map((l) => l.trim()).filter(Boolean) });
  const [collapsed, setCollapsed] = useState(false);

  const handleRemove = () => {
    if (window.confirm(`Supprimer ce bloc "${block.title || label}" ? Cette action est définitive.`)) {
      onRemove();
    }
  };

  const showHotel = block.type === "hotel";
  const showTransport = ["flight", "transfer"].includes(block.type);
  const showActivity = ["activity"].includes(block.type);
  const showMap = block.type === "map";
  const showVideo = block.type === "video";
  const showGallery = block.type === "gallery" || showHotel || showActivity;
  const showDay = block.type === "day_separator";

  // Simplified video block: just URL + preview
  if (showVideo) {
    const vid = youtubeId(block.video_url);
    return (
      <article data-testid={`block-${block.id}`} className="bg-white border border-[#EAE8E3] p-5 space-y-4">
        <BlockHeader Icon={Icon} label={label} idx={idx} total={total} onMove={onMove} onRemove={handleRemove} onDuplicate={onDuplicate} collapsed={collapsed} setCollapsed={setCollapsed} dragHandle={dragHandle} block={block} />
        {!collapsed && (<>
        <Field label="URL YouTube (embed ou normale)">
          <Input
            value={block.video_url || ""}
            onChange={(e) => onChange({ video_url: e.target.value })}
            className="luxury-input"
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid={`video-url-${block.id}`}
          />
        </Field>
        {vid && (
          <div className="aspect-video border border-[#EAE8E3]">
            <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${vid}`} title="Aperçu" frameBorder="0" allowFullScreen />
          </div>
        )}
        </>)}
      </article>
    );
  }

  return (
    <article data-testid={`block-${block.id}`} className="bg-white border border-[#EAE8E3] p-5 space-y-4">
      <BlockHeader Icon={Icon} label={label} idx={idx} total={total} onMove={onMove} onRemove={handleRemove} onDuplicate={onDuplicate} collapsed={collapsed} setCollapsed={setCollapsed} dragHandle={dragHandle} block={block} />
      {!collapsed && (<>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Titre"><Input value={block.title || ""} onChange={(e) => onChange({ title: e.target.value })} className="luxury-input" /></Field>
        <Field label="Sous-titre"><Input value={block.subtitle || ""} onChange={(e) => onChange({ subtitle: e.target.value })} className="luxury-input" /></Field>
      </div>

      {showDay && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" value={block.date || ""} onChange={(e) => onChange({ date: e.target.value })} className="luxury-input" /></Field>
        </div>
      )}

      <Field label="Description">
        <Textarea value={block.description || ""} onChange={(e) => onChange({ description: e.target.value })} rows={3} className="luxury-input resize-none" />
      </Field>

      {showTransport && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Départ (Google Places)"><PlacesAutocomplete value={block.departure} onTextChange={(v) => onChange({ departure: v })} onSelect={(p) => onChange({ departure: p.name, departure_place: p })} placeholder="Paris Orly" testid={`departure-${block.id}`} /></Field>
          <Field label="Arrivée (Google Places)"><PlacesAutocomplete value={block.arrival} onTextChange={(v) => onChange({ arrival: v })} onSelect={(p) => onChange({ arrival: p.name, arrival_place: p })} placeholder="Papeete Faa'a" testid={`arrival-${block.id}`} /></Field>
          <Field label="Heure départ"><Input type="time" value={block.time_start || ""} onChange={(e) => onChange({ time_start: e.target.value })} className="luxury-input" /></Field>
          <Field label="Heure arrivée"><Input type="time" value={block.time_end || ""} onChange={(e) => onChange({ time_end: e.target.value })} className="luxury-input" /></Field>
          <Field label="Durée"><Input value={block.duration || ""} onChange={(e) => onChange({ duration: e.target.value })} className="luxury-input" placeholder="14h 35min" /></Field>
          <Field label="Compagnie / Type"><Input value={block.carrier || ""} onChange={(e) => onChange({ carrier: e.target.value })} className="luxury-input" placeholder="Singapore Airlines" /></Field>
          <Field label="N° / Réf."><Input value={block.reference || ""} onChange={(e) => onChange({ reference: e.target.value })} className="luxury-input" placeholder="SQ333" /></Field>
        </div>
      )}

      {showHotel && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Check-in"><Input type="date" value={block.check_in || ""} onChange={(e) => onChange({ check_in: e.target.value })} className="luxury-input" /></Field>
          <Field label="Check-out"><Input type="date" value={block.check_out || ""} onChange={(e) => onChange({ check_out: e.target.value })} className="luxury-input" /></Field>
          <Field label="Nuits"><Input type="number" value={block.nights ?? ""} onChange={(e) => onChange({ nights: e.target.value ? parseInt(e.target.value, 10) : null })} className="luxury-input" /></Field>
          <Field label="Adresse (Google Places)"><PlacesAutocomplete value={block.address} onTextChange={(v) => onChange({ address: v, map_query: v || block.map_query })} onSelect={(p) => onChange({ address: p.address || p.name, place: p, map_query: p.name })} placeholder="Conrad Bora Bora Nui" testid={`address-${block.id}`} /></Field>
          <Field label="Catégorie (étoiles)"><Input type="number" step="0.1" value={block.rating ?? ""} onChange={(e) => onChange({ rating: e.target.value ? parseFloat(e.target.value) : null })} className="luxury-input" /></Field>
          <Field label="Inclus (1 par ligne)"><Textarea value={(block.included || []).join("\n")} onChange={(e) => onChange({ included: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={2} className="luxury-input resize-none" /></Field>
        </div>
      )}

      {showActivity && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Date"><Input type="date" value={block.date || ""} onChange={(e) => onChange({ date: e.target.value })} className="luxury-input" /></Field>
          <Field label="Heure"><Input type="time" value={block.time_start || ""} onChange={(e) => onChange({ time_start: e.target.value })} className="luxury-input" /></Field>
          <Field label="Durée"><Input value={block.duration || ""} onChange={(e) => onChange({ duration: e.target.value })} className="luxury-input" placeholder="3h" /></Field>
          <Field label="Lieu (Google Places)"><PlacesAutocomplete value={block.location} onTextChange={(v) => onChange({ location: v, map_query: v || block.map_query })} onSelect={(p) => onChange({ location: p.name, place: p, map_query: p.name })} placeholder="Moorea Dolphin Center" testid={`location-${block.id}`} /></Field>
        </div>
      )}

      {showMap && (
        <Field label="Requête Google Maps (adresse ou nom)"><Input value={block.map_query || ""} onChange={(e) => onChange({ map_query: e.target.value })} className="luxury-input" placeholder="Soneva Fushi, Baa Atoll" /></Field>
      )}

      {showGallery && (
        <div className="space-y-2">
          <Label className="label-eyebrow">Images (URLs ou upload)</Label>
          <Textarea
            value={(block.images || []).join("\n")}
            onChange={(e) => setImages(e.target.value)}
            rows={3}
            className="luxury-input resize-none font-mono text-xs"
            placeholder="https://...&#10;https://..."
          />
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                const urls = [];
                for (const f of files) {
                  const fd = new FormData();
                  fd.append("file", f);
                  try {
                    // Upload binary -> object storage (returns a small storage path)
                    const { data } = await api.post("/admin/proposals/tools/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    urls.push(`/api/public/files/${data.storage_path}`);
                  } catch (err) {
                    const detail = err?.response?.data?.detail || `Upload échoué : ${f.name}`;
                    toast.error(typeof detail === "string" ? detail : `Upload échoué : ${f.name}`);
                  }
                }
                // Merge the lightweight URLs onto the FRESHEST block (functional patch) so a
                // concurrent title/subtitle edit during the upload isn't clobbered.
                if (urls.length) {
                  onChange((cur) => ({ images: [...(cur.images || []), ...urls] }));
                  toast.success(`${urls.length} fichier(s) ajouté(s).`);
                }
                e.target.value = "";
              }}
              className="text-xs file:mr-2 file:px-3 file:py-1.5 file:border file:border-[#1A1B18] file:bg-white hover:file:bg-[#1A1B18] hover:file:text-white file:cursor-pointer file:text-[10px] file:uppercase file:tracking-wider file:rounded-none"
              data-testid={`upload-${block.id}`}
            />
            <span className="text-[10px] text-[#737373]">JPG · PNG · WEBP · PDF · 5 Mo max</span>
          </div>
          {(block.images || []).length > 0 && (
            <div className="flex gap-2 flex-wrap pt-2">
              {block.images.map((url, i) => (
                <div key={i} className="relative">
                  <img src={url} alt="" className="w-16 h-16 object-cover border border-[#EAE8E3]" />
                  <button
                    type="button"
                    onClick={() => onChange((cur) => ({ images: (cur.images || []).filter((_, j) => j !== i) }))}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#1A1B18] text-white text-xs flex items-center justify-center"
                    title="Retirer"
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Attachments — available on every block */}
      <div className="pt-3 border-t border-[#EAE8E3]">
        <AttachmentUploader attachments={block.attachments || []} onChange={(updater) => onChange(typeof updater === "function" ? (cur) => ({ attachments: updater(cur.attachments || []) }) : { attachments: updater })} testid={`attach-${block.id}`} />
      </div>

      {/* Pricing */}
      <div className="grid grid-cols-3 gap-3 pt-3 border-t border-[#EAE8E3]">
        <Field label="Prix public"><Input type="number" value={block.public_price ?? ""} onChange={(e) => onChange({ public_price: e.target.value ? parseFloat(e.target.value) : null })} className="luxury-input" /></Field>
        <Field label="Prix Deal"><Input type="number" value={block.deal_price ?? ""} onChange={(e) => onChange({ deal_price: e.target.value ? parseFloat(e.target.value) : null })} className="luxury-input" /></Field>
        <Field label="Libellé (ex. par personne)"><Input value={block.price_label || ""} onChange={(e) => onChange({ price_label: e.target.value })} className="luxury-input" /></Field>
      </div>
      </>)}
    </article>
  );
};

const BlockHeader = ({ Icon, label, idx, total, onMove, onRemove, onDuplicate, collapsed, setCollapsed, dragHandle, block }) => (
  <header className="flex items-center justify-between">
    <div className="flex items-center gap-2.5">
      <button {...(dragHandle || {})} className="cursor-grab active:cursor-grabbing p-1 text-[#737373] hover:text-[#1A1B18]" title="Déplacer">
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="w-7 h-7 rounded-full bg-[#FAF9F6] border border-[#EAE8E3] flex items-center justify-center"><Icon className="h-3.5 w-3.5 text-[#96836B]" /></span>
      <span className="text-[11px] uppercase tracking-[0.22em] text-[#96836B]">{label}</span>
      <span className="text-xs text-[#737373]">#{idx + 1}</span>
      {collapsed && block?.title && <span className="text-sm text-[#1A1B18] ml-2 truncate max-w-[280px]">— {block.title}</span>}
    </div>
    <div className="flex items-center gap-1">
      <button onClick={() => setCollapsed((c) => !c)} className="p-1.5 hover:bg-[#FAF9F6]" title={collapsed ? "Développer" : "Réduire"}>
        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      <button onClick={() => onMove(-1)} disabled={idx === 0} className="p-1.5 hover:bg-[#FAF9F6] disabled:opacity-30" title="Monter"><ChevronUp className="h-4 w-4" /></button>
      <button onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1.5 hover:bg-[#FAF9F6] disabled:opacity-30" title="Descendre"><ChevronDown className="h-4 w-4" /></button>
      <button onClick={onDuplicate} className="p-1.5 hover:bg-[#FAF9F6]" title="Dupliquer"><Copy className="h-4 w-4 text-[#96836B]" /></button>
      <button onClick={onRemove} className="p-1.5 hover:bg-[#FBEDE7] text-[#7A4B3B]" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
    </div>
  </header>
);
